'use client';

import React from 'react';
import { useField } from 'formik';
import type { Block, FieldOption } from '@/app/utils/get-form-schema';
import { useAuth } from '@/contexts/AuthContext';
import LoginForm from '@/components/Layout/Header/Auth/LoginForm';
import SignUpForm from '@/components/Layout/Header/Auth/SignUpForm';
import { clearTempSkipOnboarding, setTempSkipOnboarding } from '@/helpers/SkipOnboarding';
import { MAX_FORM_UPLOAD_BYTES } from '@/helpers/Constants';
import DSButton from '@/components/design-system/Button';
import DSIcon from '@/components/design-system/Icon';
import DSInput, { PreSelection, fieldControlClass } from '@/components/design-system/Input';
import { Checkbox } from '@/components/design-system/SelectionControls';
import { useWizardNav } from './WizardNavContext';

/**
 * The CMS form blocks, on the design system.
 *
 * Every control here used to be drawn locally: one `inputCls` string of
 * `rounded-md border-neutral-300 text-sm` shared by the text, textarea and
 * select fields, a hand-rolled 22px checkbox inside a `rounded-2xl` option
 * card, a bare `<input type="checkbox">` for consent, a `rounded-full` black
 * submit, and a set of inline style objects carrying hex colours (`#5B666C`,
 * `#8F97AA`, `#309EED`) that appear in no ramp. They are the DS field, the DS
 * checkbox, the DS segmented group and DS buttons now.
 *
 * One deliberate deviation from §7.2: labels sit ABOVE the field rather than
 * right-aligned inside it. The in-field label is for a short field name; these
 * are authored in the CMS and come with a subtitle and help text beneath, none
 * of which fits inside a 48px box.
 *
 * Logic is untouched: same formik keys, same validation surface, same upload
 * flow, same wizard navigation.
 */

/**
 * The author's autofill choice for a field — an HTML `autocomplete` token
 * picked from a fixed list in the builder. Undefined leaves the attribute off
 * entirely, so the browser keeps using its own heuristics.
 */
const autoFill = (block: Block): string | undefined => (block.props.autocomplete as string) || undefined;

/**
 * The Address block is one field made of several inputs, so its parts carry the
 * standard tokens automatically instead of being configured. Kept in sync with
 * the CMS shared types (separate repo).
 */
const ADDRESS_AUTOCOMPLETE: Record<string, string> = {
    line1: 'address-line1',
    line2: 'address-line2',
    city: 'address-level2',
    state: 'address-level1',
    postalCode: 'postal-code',
    country: 'country-name'
};

/** Validation messages, wherever they appear. */
const ERROR_CLS = 'text-body-600 mt-[4px] text-accent-red';

function FieldShell({ block, children, htmlFor }: { block: Block; children: React.ReactNode; htmlFor?: string }) {
    const [, meta] = useField(block.props.key ?? block.id);
    const required = block.type === 'consent' ? true : Boolean(block.props.required);
    return (
        <div>
            {block.props.label && block.props.showLabel !== false ? (
                <label htmlFor={htmlFor} className="text-body-400 block text-ink-400">
                    {block.props.label}
                    {required ? <span className="text-accent-red"> *</span> : null}
                </label>
            ) : null}
            {block.props.subtitle ? <p className="text-body-400 mt-[2px] text-ink-300">{block.props.subtitle}</p> : null}
            <div className="mt-[8px]">{children}</div>
            {block.props.helpText ? <p className="text-body-600 mt-[4px] text-ink-300">{block.props.helpText}</p> : null}
            {meta.touched && meta.error ? <p className={ERROR_CLS}>{meta.error}</p> : null}
        </div>
    );
}

function TextLike({ block, htmlType }: { block: Block; htmlType: string }) {
    const key = block.props.key ?? block.id;
    const [field, meta] = useField(key);
    return (
        <FieldShell block={block} htmlFor={key}>
            <DSInput id={key} type={htmlType} invalid={Boolean(meta.touched && meta.error)} placeholder={block.props.placeholder} autoComplete={autoFill(block)} {...field} value={field.value ?? ''} />
        </FieldShell>
    );
}

function TextArea({ block }: { block: Block }) {
    const key = block.props.key ?? block.id;
    const [field, meta] = useField(key);
    return (
        <FieldShell block={block} htmlFor={key}>
            <DSInput multiline id={key} invalid={Boolean(meta.touched && meta.error)} placeholder={block.props.placeholder} autoComplete={autoFill(block)} {...field} value={field.value ?? ''} />
        </FieldShell>
    );
}

function SelectField({ block }: { block: Block }) {
    const key = block.props.key ?? block.id;
    const [field, meta] = useField(key);
    const options: FieldOption[] = block.props.options ?? [];
    return (
        <FieldShell block={block} htmlFor={key}>
            {/* The field's chrome with the native select inside it, which is what
                `children` and the exported control class are for: a select
                cannot be a DSInput's own element, and rebuilding the border and
                padding around it by hand is how they drift.

                `bg-none` because @tailwindcss/forms paints its own chevron as a
                background image on every select, which sat beside the DS one. */}
            <DSInput invalid={Boolean(meta.touched && meta.error)} icon={<DSIcon name="chevron-down" size={20} />}>
                <select id={key} className={`${fieldControlClass} appearance-none bg-none`} autoComplete={autoFill(block)} {...field} value={field.value ?? ''}>
                    <option value="">{block.props.placeholder || 'Select…'}</option>
                    {options.map((o) => {
                        const val = o.value ?? o.label;
                        return (
                            <option key={val} value={val}>
                                {o.label}
                            </option>
                        );
                    })}
                </select>
            </DSInput>
        </FieldShell>
    );
}

/** Selectable option card (shared by radio + checkbox). Square selector fills on select. */
function OptionCard({ selected, label, description, onClick }: { selected: boolean; label: string; description?: string; onClick: () => void }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={selected}
            className={`flex w-full items-start gap-[12px] rounded-ds border px-[20px] py-[16px] text-left transition-colors duration-press ease-ds-out ${selected ? 'border-surface-600 bg-surface-200' : 'border-surface-300 bg-surface-000 hover:border-surface-400'}`}
        >
            {/* The DS checkbox box at its drawn 16px, rather than the 22px one
                this had: §7.3 sizes every selection control the same. */}
            <span className={`mt-[2px] flex size-[16px] shrink-0 items-center justify-center rounded-ds border transition-colors ${selected ? 'border-surface-600 bg-surface-600 text-ink-100' : 'border-surface-300 bg-surface-000'}`}>
                {selected ? (
                    <svg width="10" height="8" viewBox="0 0 12 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.3327 0.666626L3.99935 7.99996L0.666016 4.66663" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                ) : null}
            </span>
            <span className="flex min-w-0 flex-col">
                <span className="text-body-400 text-ink-400">{label}</span>
                {description ? <span className="text-body-600 mt-[2px] text-ink-300">{description}</span> : null}
            </span>
        </button>
    );
}

function RadioGroup({ block }: { block: Block }) {
    const key = block.props.key ?? block.id;
    const [field, , helpers] = useField(key);
    const options: FieldOption[] = block.props.options ?? [];
    return (
        <FieldShell block={block}>
            <div className="flex flex-col gap-[12px]">
                {options.map((o) => {
                    const val = o.value ?? o.label;
                    return <OptionCard key={val} selected={field.value === val} label={o.label} description={o.description} onClick={() => helpers.setValue(val)} />;
                })}
            </div>
        </FieldShell>
    );
}

function CheckboxGroup({ block }: { block: Block }) {
    const key = block.props.key ?? block.id;
    const [field, , helpers] = useField<string[]>(key);
    const options: FieldOption[] = block.props.options ?? [];
    const value: string[] = Array.isArray(field.value) ? field.value : [];
    const toggle = (v: string) => helpers.setValue(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
    return (
        <FieldShell block={block}>
            <div className="flex flex-col gap-[12px]">
                {options.map((o) => {
                    const val = o.value ?? o.label;
                    return <OptionCard key={val} selected={value.includes(val)} label={o.label} description={o.description} onClick={() => toggle(val)} />;
                })}
            </div>
        </FieldShell>
    );
}

/** Yes/No choice (two buttons) with an optional follow-up text box. */
function YesNoField({ block }: { block: Block }) {
    const key = block.props.key ?? block.id;
    const textKey = `${key}_text`;
    const [choice, , choiceHelpers] = useField<string>(key);
    const [textField, textMeta] = useField<string>(textKey);
    const noLabel = (block.props.noLabel as string) || 'No';
    const yesLabel = (block.props.yesLabel as string) || 'Yes';
    const showTextOn = (block.props.showTextOn as string) || 'never';
    const showText = showTextOn !== 'never' && (showTextOn === 'both' || choice.value === showTextOn);
    const options: Array<['no' | 'yes', string]> = [
        ['no', noLabel],
        ['yes', yesLabel]
    ];
    return (
        <FieldShell block={block}>
            {/* `Pre-Selection Input` (§7.2): the segmented group of two or three
                the system already draws for exactly this. */}
            <PreSelection
                options={options.map(([opt, lbl]) => ({ value: opt, label: lbl }))}
                value={choice.value as 'no' | 'yes'}
                onChange={(next) => choiceHelpers.setValue(next)}
            />
            {showText ? (
                <div className="mt-[12px]">
                    <DSInput
                        multiline
                        invalid={Boolean(textMeta.touched && textMeta.error)}
                        placeholder={(block.props.textPlaceholder as string) || ''}
                        {...textField}
                        value={textField.value ?? ''}
                    />
                    {textMeta.touched && textMeta.error ? <p className={ERROR_CLS}>{textMeta.error}</p> : null}
                </div>
            ) : null}
        </FieldShell>
    );
}

function ConsentField({ block }: { block: Block }) {
    const key = block.props.key ?? block.id;
    const [field, meta, helpers] = useField(key);
    return (
        <div>
            {/* `!items-start` because the DS checkbox centres its label, and a
                consent line is usually two or three lines long. */}
            <Checkbox
                className="!items-start"
                checked={Boolean(field.value)}
                onChange={(e) => helpers.setValue(e.target.checked)}
                label={(
                    <span className="text-body-400 text-ink-400">
                        {block.props.label || 'I agree'} <span className="text-accent-red">*</span>
                    </span>
                )}
            />
            {meta.touched && meta.error ? <p className={ERROR_CLS}>{meta.error}</p> : null}
        </div>
    );
}

function AddressField({ block }: { block: Block }) {
    const key = block.props.key ?? block.id;
    const parts = [
        { sub: 'line1', label: 'Address line 1' },
        { sub: 'line2', label: 'Address line 2' },
        { sub: 'city', label: 'City' },
        { sub: 'state', label: 'State / Region' },
        { sub: 'postalCode', label: 'Postal code' },
        { sub: 'country', label: 'Country' }
    ];
    return (
        <FieldShell block={block}>
            <div className="grid grid-cols-2 gap-[8px]">
                {parts.map((p) => (
                    // eslint-disable-next-line @typescript-eslint/no-use-before-define
                    <AddressSub key={p.sub} name={`${key}.${p.sub}`} placeholder={p.label} full={p.sub === 'line1' || p.sub === 'line2'} autoComplete={ADDRESS_AUTOCOMPLETE[p.sub]} />
                ))}
            </div>
        </FieldShell>
    );
}
function AddressSub({ name, placeholder, full, autoComplete }: { name: string; placeholder: string; full?: boolean; autoComplete?: string }) {
    const [field] = useField(name);
    return <DSInput containerClassName={full ? 'col-span-2' : undefined} placeholder={placeholder} autoComplete={autoComplete} {...field} value={field.value ?? ''} />;
}

interface UploadedFile {
    // Storage key in dedicated form-uploads/ storage (the stable identifier the
    // submission keeps). Not a Media Library id.
    key: string;
    name: string;
    url?: string;
    size?: number;
    mime?: string;
}
interface Pending {
    tempId: string;
    name: string;
    progress: number;
}

/** Upload files to the Next proxy with XHR (for progress); accumulate into the field value. */
function useUploader(value: UploadedFile[], setValue: (v: UploadedFile[]) => void) {
    const [pending, setPending] = React.useState<Pending[]>([]);
    const [error, setError] = React.useState<string | null>(null);
    // Ref is the source of truth so concurrent completions accumulate correctly.
    const ref = React.useRef<UploadedFile[]>(value);
    ref.current = value;

    const addFiles = React.useCallback(
        (files: FileList | File[]) => {
            setError(null);
            Array.from(files).forEach((file) => {
                /* Caught here so a too-large file is refused instantly, rather
                   than after uploading it only to be turned away. The route
                   checks the same limit; this one is for the person. */
                if (file.size > MAX_FORM_UPLOAD_BYTES) {
                    const mb = (Math.ceil((file.size / 1024 / 1024) * 10) / 10).toFixed(1);
                    setError(`${file.name}: ${mb}MB is over the ${MAX_FORM_UPLOAD_BYTES / 1024 / 1024}MB limit.`);
                    return;
                }
                const tempId = Math.random().toString(36).slice(2);
                setPending((p) => [...p, { tempId, name: file.name, progress: 0 }]);
                const xhr = new XMLHttpRequest();
                xhr.open('POST', '/api/forms/upload');
                xhr.upload.onprogress = (e) => {
                    if (e.lengthComputable) setPending((p) => p.map((x) => (x.tempId === tempId ? { ...x, progress: Math.round((e.loaded / e.total) * 100) } : x)));
                };
                xhr.onload = () => {
                    setPending((p) => p.filter((x) => x.tempId !== tempId));
                    if (xhr.status >= 200 && xhr.status < 300) {
                        try {
                            const media = JSON.parse(xhr.responseText) as UploadedFile;
                            ref.current = [...ref.current, media];
                            setValue(ref.current);
                        } catch {
                            setError(`${file.name}: upload succeeded but the response was unreadable.`);
                        }
                    } else {
                        // Surface the server's reason instead of dropping the file silently.
                        let msg = `Upload failed (${xhr.status}).`;
                        try {
                            const body = JSON.parse(xhr.responseText);
                            if (body?.error) msg = body.error;
                        } catch {
                            /* keep the status-based message */
                        }
                        setError(`${file.name}: ${msg}`);
                    }
                };
                xhr.onerror = () => {
                    setPending((p) => p.filter((x) => x.tempId !== tempId));
                    setError(`${file.name}: network error while uploading. Please try again.`);
                };
                const fd = new FormData();
                fd.append('file', file);
                xhr.send(fd);
            });
        },
        [setValue]
    );

    const remove = React.useCallback(
        (fileKey: string) => {
            ref.current = ref.current.filter((f) => f.key !== fileKey);
            setValue(ref.current);
        },
        [setValue]
    );

    return { pending, addFiles, remove, error };
}

/** Photo-grid upload: tiles of thumbnails/checks + "+" slots. */
function FileGridField({ block }: { block: Block }) {
    const key = block.props.key ?? block.id;
    const [field, , helpers] = useField<UploadedFile[]>(key);
    const value = Array.isArray(field.value) ? field.value : [];
    const { pending, addFiles, remove, error } = useUploader(value, (v) => helpers.setValue(v));
    const inputRef = React.useRef<HTMLInputElement>(null);
    const max = Number(block.props.max) || 8;
    const accept = (block.props.accept as string) || 'image/*';
    const filled = value.length + pending.length;
    const emptySlots = Math.max(0, max - filled);

    // 5 tiles per row, sized to fit the form width (was fixed 88px, which
    // overflowed a 480px form and wrapped). Tiles stay square via aspect-square.
    const tile = 'flex aspect-square w-full items-center justify-center rounded-ds';
    return (
        <FieldShell block={block}>
            <div className="grid grid-cols-5 gap-[12px]">
                {value.map((f) => (
                    <div key={f.key} className={`${tile} relative border border-surface-300 bg-surface-100 text-accent-green`} title={f.name}>
                        <svg viewBox="0 0 24 24" fill="none" className="size-[24px]" aria-hidden>
                            <path d="M20 6 9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <button type="button" onClick={() => remove(f.key)} className="text-body-600 absolute right-[4px] top-[4px] flex size-[20px] items-center justify-center rounded-ds bg-surface-600 text-ink-100" aria-label="Remove">
                            ×
                        </button>
                    </div>
                ))}
                {pending.map((p) => (
                    <div key={p.tempId} className={`${tile} text-body-600 border border-surface-300 bg-surface-100 text-ink-300`}>{p.progress}%</div>
                ))}
                {/* Interchangeable, stateless placeholder slots generated from a
                    count — the index is the only identity they have. */}
                {Array.from({ length: emptySlots === 0 ? 0 : Math.max(1, emptySlots) }).map((_, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <button key={`add-${i}`} type="button" onClick={() => inputRef.current?.click()} className={`${tile} border border-surface-300 bg-surface-000 text-ink-300 transition-colors duration-press ease-ds-out hover:border-surface-400`}>
                        <DSIcon name="plus" size={20} />
                    </button>
                ))}
            </div>
            {error ? <p className={ERROR_CLS}>{error}</p> : null}
            <input ref={inputRef} type="file" accept={accept} multiple className="hidden" onChange={(e) => { if (e.target.files) addFiles(e.target.files); e.target.value = ''; }} />
        </FieldShell>
    );
}

/** Document dropzone: large add area + a list of files with progress. */
function FileDropzoneField({ block }: { block: Block }) {
    const key = block.props.key ?? block.id;
    const [field, , helpers] = useField<UploadedFile[]>(key);
    const value = Array.isArray(field.value) ? field.value : [];
    const { pending, addFiles, remove, error } = useUploader(value, (v) => helpers.setValue(v));
    const inputRef = React.useRef<HTMLInputElement>(null);
    const accept = (block.props.accept as string) || 'image/*,.pdf';

    return (
        <FieldShell block={block}>
            <div
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files) addFiles(e.dataTransfer.files); }}
                className="text-body-400 flex min-h-[120px] cursor-pointer items-center justify-center gap-[8px] rounded-ds border border-dashed border-surface-300 bg-surface-100 text-ink-300 transition-colors duration-press ease-ds-out hover:bg-surface-200"
            >
                <DSIcon name="plus" size={20} />
                Add PDF or image document
            </div>
            {(value.length > 0 || pending.length > 0) && (
                <div className="mt-[12px] flex flex-col gap-[8px]">
                    {value.map((f) => (
                        <div key={f.key} className="text-body-400 flex items-center justify-between rounded-ds border border-surface-300 px-[16px] py-[12px] text-ink-400">
                            <span className="truncate">{f.name}</span>
                            <button type="button" onClick={() => remove(f.key)} className="ml-[12px] text-ink-300 transition-colors hover:text-ink-400" aria-label="Remove">×</button>
                        </div>
                    ))}
                    {pending.map((p) => (
                        <div key={p.tempId} className="text-body-400 flex items-center justify-between rounded-ds border border-surface-300 px-[16px] py-[12px] text-ink-400">
                            <span className="truncate">{p.name}</span>
                            <span className="text-body-500 ml-[12px] rounded-ds bg-surface-200 px-[8px] py-[2px] text-ink-300">{p.progress}%</span>
                        </div>
                    ))}
                </div>
            )}
            {error ? <p className={ERROR_CLS}>{error}</p> : null}
            <input ref={inputRef} type="file" accept={accept} multiple className="hidden" onChange={(e) => { if (e.target.files) addFiles(e.target.files); e.target.value = ''; }} />
        </FieldShell>
    );
}

/** Default list bullet — the system's check glyph, on the ink ramp. */
function BulletCheck() {
    return (
        <span aria-hidden className="mt-1 shrink-0">
            <svg width="12" height="9" viewBox="0 0 12 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.3327 0.666626L3.99935 7.99996L0.666016 4.66663" stroke="#309EED" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        </span>
    );
}

function Heading({ block }: { block: Block }) {
    const level = Number(block.props.level) || 2;
    const Tag = (`h${level}` as unknown) as keyof React.JSX.IntrinsicElements;
    // H1 and H2 share the form's title size; H3 steps down one token.
    const headingEl = level === 1 || level === 2 ? <Tag className="text-h1 text-ink-400">{block.props.text}</Tag> : <Tag className="text-h2 text-ink-400">{block.props.text}</Tag>;
    const subtitle = block.props.subtitle as string | undefined;
    if (!subtitle) return headingEl;
    return (
        <div>
            {headingEl}
            <p className="text-body-400 mt-[12px] text-ink-300">{subtitle}</p>
        </div>
    );
}

function ListBlock({ block }: { block: Block }) {
    const title = block.props.title as string | undefined;
    const items = (Array.isArray(block.props.items) ? block.props.items : []) as Array<{ text?: string; title?: string; description?: string }>;
    return (
        <div>
            {title ? <p className="text-body-100 mb-[16px] text-ink-400">{title}</p> : null}
            <div className="flex flex-col gap-[16px]">
                {items.map((it, i) => {
                    const text = it.text ?? it.description ?? '';
                    return (
                        // Static CMS list — never reordered or filtered, and the
                        // rows hold no state, so the index is a safe identity.
                        // Content can repeat, so keying on it risks duplicates.
                        // eslint-disable-next-line react/no-array-index-key
                        <div key={i} className="flex gap-[12px]">
                            <BulletCheck />
                            <div className="flex flex-col">
                                {it.title ? <p className="text-body-400 text-ink-400">{it.title}</p> : null}
                                {text ? <p className="text-body-400 text-ink-300">{text}</p> : null}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function Video({ block }: { block: Block }) {
    const url: string = block.props.url ?? '';
    if (!url) return null;
    const embed = /youtube|youtu\.be|vimeo/.test(url);
    return (
        <div className="aspect-video w-full overflow-hidden rounded-ds bg-surface-600">
            {embed ? <iframe src={url.replace('watch?v=', 'embed/')} className="h-full w-full" allowFullScreen title="video" /> : <video src={url} controls className="h-full w-full" />}
        </div>
    );
}

/** Renders a single block. Input blocks are formik-bound; content blocks are static. */
export function BlockView({ block }: { block: Block }) {
    switch (block.type) {
        case 'heading':
            return <Heading block={block} />;
        case 'richtext':
            return <p className="text-body-400 whitespace-pre-wrap text-ink-300">{block.props.body}</p>;
        case 'list':
            return <ListBlock block={block} />;
        case 'link':
            return block.props.text ? (
                <a href={(block.props.href as string) || '#'} className="text-body-400 text-ink-300 underline-offset-2 hover:underline">
                    {block.props.text}
                </a>
            ) : null;
        case 'video':
            return <Video block={block} />;
        case 'image':
            // eslint-disable-next-line @next/next/no-img-element
            return block.props.url ? <img src={block.props.url} alt={block.props.alt ?? ''} className="w-full rounded-ds" /> : null;
        case 'divider':
            return <hr className="border-surface-300" />;
        case 'text':
            return <TextLike block={block} htmlType="text" />;
        case 'email':
            return <TextLike block={block} htmlType="email" />;
        case 'phone':
            return <TextLike block={block} htmlType="tel" />;
        case 'number':
        case 'currency':
            return <TextLike block={block} htmlType="number" />;
        case 'date':
            return <TextLike block={block} htmlType="date" />;
        case 'textarea':
            return <TextArea block={block} />;
        case 'select':
            return <SelectField block={block} />;
        case 'radio':
            return <RadioGroup block={block} />;
        case 'multiselect':
        case 'checkbox':
            return <CheckboxGroup block={block} />;
        case 'yesno':
            return <YesNoField block={block} />;
        case 'consent':
            return <ConsentField block={block} />;
        case 'address':
            return <AddressField block={block} />;
        case 'file':
            return block.props.variant === 'dropzone' ? <FileDropzoneField block={block} /> : <FileGridField block={block} />;
        case 'payment':
            return (
                <div className="text-body-400 rounded-ds border border-dashed border-surface-300 p-[16px] text-ink-300">
                    Payment step{block.props.amount ? ` ($${(Number(block.props.amount) / 100).toFixed(2)})` : ''} (Stripe wiring is a later phase).
                </div>
            );
        case 'review':
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            return <ReviewSummary />;
        case 'next':
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            return <NextButton block={block} />;
        case 'auth':
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            return <AuthBlock block={block} />;
        default:
            return null;
    }
}

/**
 * Inline sign-in / sign-up gate. Reuses the app's real LoginForm/SignUpForm
 * (all required providers already wrap the wizard on live pages). When the
 * visitor is signed in it shows a confirmation; the wizard reads the same
 * `useAuth().user` to unlock the step's Next button. No modal, no redirect,
 * except sign-up may navigate to /onboarding when NEXT_PUBLIC_ONBOARDING is on
 * (see note in the wizard docs).
 */
function AuthBlock({ block }: { block: Block }) {
    const auth = useAuth();
    const user = auth?.user;
    const [mode, setMode] = React.useState<'login' | 'signup'>(block.props.defaultMode === 'signup' ? 'signup' : 'login');
    // Default on: this gate sits mid-wizard, so navigating to /onboarding would
    // throw away the visitor's answers.
    const skipOnboarding = block.props.skipOnboarding !== false;

    // The auth forms are told directly (below) — that's what actually decides
    // it. This tab-wide flag is kept in step as well, because it's what any
    // OTHER sign-up surface on the page reads. Written in both directions:
    // only ever setting it left it stuck on for the rest of the tab session,
    // which made turning this toggle off look like it did nothing.
    React.useLayoutEffect(() => {
        if (skipOnboarding) setTempSkipOnboarding();
        else clearTempSkipOnboarding();
    }, [skipOnboarding]);

    if (user) {
        return (
            <div className="rounded-ds border border-surface-300 bg-surface-100 p-[16px]">
                <p className="text-body-400 text-ink-400">You&apos;re signed in.</p>
                <p className="text-body-400 text-ink-300">Continue to the next step.</p>
            </div>
        );
    }

    return <div>{mode === 'login' ? <LoginForm changeToSignup={() => setMode('signup')} skipOnboarding={skipOnboarding} /> : <SignUpForm changeToLogin={() => setMode('login')} skipOnboarding={skipOnboarding} />}</div>;
}

/** Draggable "Next" action: advances the wizard (or submits on the last step). */
function NextButton({ block }: { block: Block }) {
    const nav = useWizardNav();
    const label = (block.props.text as string) || (nav.isLast ? 'Submit' : 'Continue');
    const disabled = nav.isSubmitting || !nav.canProceed;
    return (
        // The title carries the reason a gate step is blocked, so it sits on a
        // wrapper: a disabled button does not reliably show its own tooltip.
        <div title={!nav.canProceed ? 'Sign in to continue' : undefined}>
            <DSButton
                fullWidth
                variant="primary"
                disabled={disabled}
                loading={nav.isSubmitting}
                icon={<DSIcon name="arrow-right" size={20} />}
                onClick={nav.goNext}
            >
                {label}
            </DSButton>
        </div>
    );
}

/** Render one answer: names for uploads, a comma list for multi-value answers. */
function formatAnswer(value: unknown): string {
    const one = (v: unknown) => (v && typeof v === 'object' ? ((v as { name?: string }).name ?? JSON.stringify(v)) : String(v));
    if (Array.isArray(value)) return value.map(one).join(', ');
    return one(value);
}

/**
 * Summary of the answers given so far, labelled. Reads the wizard's own list
 * rather than raw formik values so conditionally hidden fields and abandoned
 * branches don't show up — it matches what will be submitted.
 */
function ReviewSummary() {
    const { answers } = useWizardNav();
    return (
        <div className="rounded-ds bg-surface-100 p-[16px]">
            <p className="text-body-100 mb-[8px] text-ink-400">Review your answers</p>
            <dl className="text-body-400 flex flex-col gap-[4px]">
                {answers.map((a) => (
                    <div key={a.key} className="flex justify-between gap-[16px]">
                        <dt className="text-ink-300">{a.label}</dt>
                        <dd className="text-right text-ink-400">{formatAnswer(a.value)}</dd>
                    </div>
                ))}
                {answers.length === 0 ? <p className="text-ink-300">Nothing filled in yet.</p> : null}
            </dl>
        </div>
    );
}
